import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Loader2, PartyPopper, Flame, BadgePercent, Leaf, Heart, ThumbsUp, ThumbsDown, Megaphone,
} from "lucide-react";
import { getIcon } from "./iconMap";
import { LogoDock } from "./LogoDock";

// 🔎 Name-sleuth fallback: 'Other'/generic spots get an icon guessed from their name.
const NAME_ICON_GUESSES = [
  [/coffee|cafe|caf\u00e9|espresso|roaster/i, "Coffee"],
  [/pub|bar\b|tavern|brew|taproom|saloon|lounge/i, "Beer"],
  [/burger|grill|bbq|steak|diner|taco|pizza|kitchen|cantina|bistro|restaurant|eatery|sushi|wings/i, "Utensils"],
  [/bakery|donut|doughnut|bagel|pastry/i, "Croissant"],
  [/bank|trust|credit union|financial/i, "Landmark"],
  [/hotel|inn\b|suites|lodge|motel|resort/i, "BedDouble"],
  [/law|attorney|legal|defense team|justice/i, "Scale"],
  [/salon|barber|cuts\b|hair|nails|spa\b/i, "Scissors"],
  [/gym|fitness|yoga|crossfit/i, "Dumbbell"],
  [/auto|tire|car wash|motors|garage|collision/i, "Car"],
  [/pharmacy|drug ?store/i, "Pill"],
  [/pet\b|veterinar|animal/i, "PawPrint"],
  [/grocery|market|foods|supermercado/i, "ShoppingBasket"],
  [/church|chapel|ministries|temple/i, "Cross"],
  [/school|academy|university|college|learning/i, "GraduationCap"],
  [/clothing|apparel|boutique|fashion|shoes/i, "Shirt"],
  [/liquor|wine|spirits/i, "Wine"],
  [/gas|fuel|petro/i, "Fuel"],
];

const iconFor = (b) => {
  if (b.icon && b.icon !== "Sparkles") return getIcon(b.icon);
  const hit = NAME_ICON_GUESSES.find(([re]) => re.test(b.name || ""));
  return getIcon(hit ? hit[1] : b.icon);
};
import { logoFor, webUrlFor, dealUrlFor, brandDomain } from "@/lib/brandLogos";
import { GPS } from "@/constants/testIds";
import { POGZILLA_BADGE } from "@/lib/mapConstants";
import { useMarketRewards } from "@/lib/useMarketRewards";

// 📐 SPACE-FOR-RENT + rented patches flying on the strip (Sir Eric's board slots go mobile)
const RentedSlotCards = () => {
  const [slots, setSlots] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/deals-board/slots`).then((r) => setSlots(r.data.slots || [])).catch(() => {});
  }, []);
  const openBoard = () => window.dispatchEvent(new CustomEvent("airpog:open-board"));
  return (
    <>
      {slots.map((s) => (
        <button key={s.id} data-testid={`strip-slot-${s.id}`} onClick={openBoard}
          className="fade-in shrink-0 w-[158px] md:w-[178px] rounded-2xl overflow-hidden bg-slate-900 border-2 border-emerald-400/60 flex flex-col text-left p-2">
          <p className="text-[8px] font-black tracking-widest text-emerald-300">📐 PAID SPACE</p>
          <p className="flex-1 text-[11px] font-heading font-extrabold text-white leading-tight line-clamp-3 mt-1">{s.headline}</p>
          {s.detail && <p className="text-[8px] text-slate-400 font-semibold line-clamp-1">{s.detail}</p>}
          <p className="text-[8px] font-bold text-slate-500 mt-0.5">— {s.name}</p>
        </button>
      ))}
      <button data-testid="strip-rent-teaser" onClick={openBoard}
        className="fade-in shrink-0 w-[158px] md:w-[178px] rounded-2xl overflow-hidden bg-slate-900/80 border-2 border-dashed border-emerald-400/50 flex flex-col items-center justify-center text-center p-2">
        <span className="text-2xl">📐</span>
        <p className="text-[9px] font-black tracking-widest text-emerald-300 mt-1">THIS SPACE FOR RENT</p>
        <p className="text-[8px] font-bold text-slate-400">🏷 garage sales · 🚚 food trucks · 🚐 mobile biz</p>
        <p className="text-[8px] font-black text-emerald-400 mt-0.5">$5 / week — tap to grab it</p>
      </button>
    </>
  );
};

// 📅 NEXT POG EVENT — the next scheduled event rides the strip; POG'RS mark it with the 🔔
const b64ToUint8 = (b64) => {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};
const nudgeEventPush = async () => {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const reg = await navigator.serviceWorker.ready;
    if (await reg.pushManager.getSubscription()) return;
    toast("🔔 Marked! Want a ping the second it goes live?", {
      duration: 9000,
      action: {
        label: "Turn on alerts",
        onClick: async () => {
          try {
            if ((await Notification.requestPermission()) !== "granted") return;
            const { data } = await axios.get(`${BACKEND_URL}/api/push/vapid-key`);
            const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.key) });
            await axios.post(`${BACKEND_URL}/api/push/subscribe`, { subscription: sub.toJSON() }, { withCredentials: true });
            toast.success("🔔 POG alerts ON — you'll feel the blast!");
          } catch { /* blocked */ }
        },
      },
    });
  } catch { /* unsupported */ }
};
const NextEventCard = () => {
  const [ev, setEv] = useState(null);
  const anonId = localStorage.getItem("airpog_anon_id") || "";
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/pog-events/next?anon_id=${encodeURIComponent(anonId)}`)
      .then((r) => {
        const e = r.data.event;
        setEv(e);
        // 🔔 EVENT REMINDER — marked event starting within 24h pings once
        if (e && e.marked) {
          const hrs = (new Date(e.starts_at) - Date.now()) / 3600000;
          const k = `airpog_evt_reminded_${e.id}`;
          if (hrs > 0 && hrs <= 24 && !localStorage.getItem(k)) {
            localStorage.setItem(k, "1");
            toast(`🔔 ${e.emoji} ${e.title} starts in ${hrs < 1 ? `${Math.max(1, Math.round(hrs * 60))}m` : `${Math.round(hrs)}h`} — you marked it!`, { duration: 8000 });
          }
        }
      }).catch(() => {});
  }, []); // eslint-disable-line
  if (!ev) return null;
  const dt = new Date(ev.starts_at);
  const days = Math.max(0, Math.ceil((dt - Date.now()) / 86400000));
  const mark = (e) => {
    e.stopPropagation();
    if (ev.marked || !anonId) return;
    axios.post(`${BACKEND_URL}/api/pog-events/${ev.id}/mark`, { anon_id: anonId })
      .then((r) => { setEv({ ...ev, marked: true, marks: r.data.marks }); nudgeEventPush(); }).catch(() => {});
  };
  return (
    <div data-testid="next-event-card"
      className="fade-in shrink-0 w-[158px] md:w-[178px] rounded-2xl overflow-hidden bg-slate-900 border-2 border-fuchsia-400/60 flex flex-col text-left p-2">
      <p className="text-[8px] font-black tracking-widest text-fuchsia-300">📅 NEXT POG EVENT</p>
      <p className="flex-1 text-[12px] font-heading font-extrabold text-white leading-tight line-clamp-2 mt-1">{ev.emoji} {ev.title}</p>
      <p className="text-[9px] font-bold text-slate-400">{dt.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{days > 0 ? ` · in ${days}d` : " · TODAY!"}</p>
      <button data-testid="next-event-mark-btn" onClick={mark}
        className={`mt-1.5 h-7 rounded-lg text-[10px] font-heading font-black flex items-center justify-center gap-1 ${ev.marked ? "bg-emerald-500/20 text-emerald-300" : "bg-fuchsia-500 text-slate-900 active:scale-95 transition-transform"}`}>
        {ev.marked ? `✅ MARKED · ${ev.marks} going` : `🔔 MARK IT${ev.marks ? ` · ${ev.marks}` : ""}`}
      </button>
    </div>
  );
};

// 🎉 EVENT DAY TAKEOVER — live event lights the map; final hour shows a ticking countdown pill
const EventTakeover = () => {
  const [ev, setEv] = useState(null);
  const [next, setNext] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [claimed, setClaimed] = useState(false);
  useEffect(() => {
    const load = () => {
      axios.get(`${BACKEND_URL}/api/pog-events/live`).then((r) => setEv(r.data.event)).catch(() => {});
      axios.get(`${BACKEND_URL}/api/pog-events/next`).then((r) => setNext(r.data.event)).catch(() => {});
    };
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, []);
  const cdMs = next ? new Date(next.starts_at) - now : Infinity;
  const inFinalHour = !ev && next && cdMs > -60000 && cdMs <= 3600000;
  useEffect(() => {
    if (!inFinalHour) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [inFinalHour]);
  const claim = () => {
    axios.post(`${BACKEND_URL}/api/pog-events/${ev.id}/bonus`, {}, { withCredentials: true })
      .then((r) => { setClaimed(true); toast.success(`🎉 +${r.data.bucks} POG BUCKS — EVENT DAY BONUS!`); })
      .catch((e) => {
        if (e.response?.status === 401) toast("🔑 Sign in as a POG'R to claim the event bonus!");
        else { setClaimed(true); toast(e.response?.data?.detail || "Already claimed!"); }
      });
  };
  if (ev) return (
    <div data-testid="event-takeover-banner"
      className="absolute top-[7.6rem] left-1/2 -translate-x-1/2 z-[1010] flex items-center gap-2 rounded-full pl-3 pr-1.5 py-1.5 bg-gradient-to-r from-fuchsia-600/90 to-amber-500/90 shadow-[0_0_18px_rgba(217,70,239,0.6)] backdrop-blur">
      <span className="text-xs font-heading font-black text-white whitespace-nowrap">🎉 {ev.emoji} {ev.title}{ev.host_name ? ` @ ${ev.host_name}` : ""} IS LIVE!</span>
      <button data-testid="event-bonus-btn" onClick={claim} disabled={claimed}
        className="h-6 px-2.5 rounded-full bg-slate-900/80 text-amber-300 text-[10px] font-heading font-black active:scale-95 transition-transform disabled:opacity-60 whitespace-nowrap">
        {claimed ? "✅ CLAIMED" : "+10 BUCKS"}
      </button>
    </div>
  );
  if (inFinalHour) {
    const s = Math.max(0, Math.floor(cdMs / 1000));
    const mmss = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    return (
      <div data-testid="event-countdown-pill"
        className="absolute top-[7.6rem] left-1/2 -translate-x-1/2 z-[1010] flex items-center gap-2 rounded-full px-3.5 py-1.5 bg-slate-900/85 border-2 border-amber-400/70 shadow-[0_0_14px_rgba(251,191,36,0.45)] backdrop-blur">
        <span className="text-xs font-heading font-black text-amber-300 whitespace-nowrap">
          {s === 0 ? `🎉 ${next.emoji} ${next.title} STARTING NOW!` : `⏳ ${next.emoji} ${next.title}${next.host_name ? ` @ ${next.host_name}` : ""} in ${mmss}`}
        </span>
      </div>
    );
  }
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdSpotCard = ({ navigate }) => (
  <button
    data-testid="ad-spot-promo-card"
    onClick={() => navigate("/business")}
    className="fade-in shrink-0 w-[150px] md:w-[172px] cursor-pointer transition-all duration-300 hover:-translate-y-2 relative rounded-2xl overflow-hidden h-[150px] md:h-[168px] flex flex-col items-center justify-center gap-2 text-center p-4 border-2 border-dashed border-fuchsia-400/50 bg-fuchsia-500/10 hover:bg-fuchsia-500/20"
  >
    <span className="absolute top-1.5 left-2 z-10 text-[9px] font-extrabold uppercase tracking-widest text-fuchsia-200/90 bg-fuchsia-500/20 rounded px-1.5 py-0.5">Ad spot</span>
    <Megaphone className="w-7 h-7 text-fuchsia-300" />
    <span className="font-heading font-bold text-sm leading-tight text-white">Your POG Ad here</span>
    <span className="text-[11px] text-fuchsia-200/80 leading-snug">Claim your spot — list free &amp; promote</span>
  </button>
);

// Bottom business/deals strip — the scrolling card rail (extracted from GpsOverlay, Map Cleanup Stage 5).
export const BusinessStrip = ({
  stripRef, layout, loading, warming, radius, exploreActive, exploreLabel,
  businesses, shownBusinesses, activeId, onTabClick, driver,
  onFav, isFav, favBusy, voteBiz, hasVoted, navigate, spotlightIds,
  dealCount = 0, spotCount = 0, dealsOnly = false, onToggleDeals, zoneBadge, poggedZone = false,
}) => {
  const nightGlow = (() => { const h = new Date().getHours(); return h >= 19 || h < 6; })();
  // 🔄 AUTO-ROTATE: every 15s quietly cycle regular merchants into view (priority cards hold the front)
  const [rotOffset] = useState(0);
  // (15s auto-rotate shuffle retired — the rail physically rolls now; mid-roll reorders made cards vanish/teleport)
  const isPriority = (b) => b.has_deals || b.is_sale || b.featured || b.super_pog || b.ad_image_url || (spotlightIds && spotlightIds.get(b.id));
  const priorityCards = shownBusinesses.filter(isPriority);
  const regularCards = shownBusinesses.filter((b) => !isPriority(b));
  const off = regularCards.length > 1 ? rotOffset % regularCards.length : 0;
  // ⭐ MERCHANT OF THE MINUTE — one regular merchant takes the golden front slot, rotating every minute
  const motmIdx = regularCards.length ? Math.floor(Date.now() / 60000) % regularCards.length : -1;
  const motm = motmIdx >= 0 ? { ...regularCards[motmIdx], _motm: true } : null;
  const restRegulars = regularCards.filter((_, i) => i !== motmIdx);
  const rOff = restRegulars.length > 1 ? off % restRegulars.length : 0;
  const displayBusinesses = [...(motm ? [motm] : []), ...priorityCards, ...restRegulars.slice(rOff), ...restRegulars.slice(0, rOff)];
  // ⭐ beacon: count this minute's spotlight (throttled per business+minute)
  useEffect(() => {
    if (!motm?.id) return;
    const k = `airpog_motm_ping_${motm.id}_${Math.floor(Date.now() / 60000)}`;
    if (sessionStorage.getItem(k)) return;
    sessionStorage.setItem(k, "1");
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/motm/impression`, { business_id: String(motm.id), name: motm.name || "" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motm?.id]);
  // 🎛️ LOGO VIEW toggle — logo-first dock vs classic glass cards
  const [logoView, setLogoView] = useState(() => localStorage.getItem("airpog_strip_view") === "logos");
  const toggleView = () => {
    const nv = !logoView;
    setLogoView(nv);
    localStorage.setItem("airpog_strip_view", nv ? "logos" : "cards");
  };
  const { t } = useTranslation();
  // 🔍 POG FINDER — Google's last weapon, now ours: instant merchant search over the live strip
  const [finderOpen, setFinderOpen] = useState(false);
  const [q, setQ] = useState("");
  // 👁️ nearby deal count — badge on the POG VISION launcher
  const dealsNearby = businesses.filter((b) => b.has_deals && !b.ad_image_url).length;
  // 🔍 open the finder automatically after a POG WARP — explorers hunt the new city instantly
  useEffect(() => {
    const h = () => { setFinderOpen(true); setQ(""); };
    window.addEventListener("airpog:warped", h);
    return () => window.removeEventListener("airpog:warped", h);
  }, []);
  const ql = q.trim().toLowerCase();
  const finderBusinesses = ql
    ? displayBusinesses.filter((b) => !b.ad_image_url && ((b.name || "").toLowerCase().includes(ql) || (b.category_label || "").toLowerCase().includes(ql)))
    : displayBusinesses;
  const marketByBiz = useMarketRewards();
  const [shotMap, setShotMap] = useState({});
  // ⭐ Batch star ratings — trust signals on the cyber cards
  const [starsMap, setStarsMap] = useState({});
  const starIdsKey = displayBusinesses.slice(0, 60).map((b) => b.id).join(",");
  useEffect(() => {
    const ids = starIdsKey ? starIdsKey.split(",") : [];
    if (!ids.length) return;
    axios.post(`${BACKEND_URL}/api/deal-reviews/stars`, { ids })
      .then((r) => setStarsMap(r.data.stars || {})).catch(() => {});
  }, [starIdsKey]); // eslint-disable-line
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/shots/active`).then((r) => {
      const m = {};
      (r.data.shots || []).forEach((s) => { m[s.business_id] = s; });
      setShotMap(m);
    }).catch(() => {});
  }, []);
  if (logoView) {
    return (
      <>
        <EventTakeover />
        <LogoDock businesses={displayBusinesses.filter((b) => !b.ad_image_url)} onTabClick={onTabClick} onFav={onFav} isFav={isFav} onToggleView={toggleView} />
      </>
    );
  }
  return (
  <>
  <EventTakeover />
  <style>{`
    .cyber-card{background:rgba(24,24,26,0.42);backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);border:1px solid var(--cyb);box-shadow:0 0 10px var(--cybg),0 0 20px var(--cybg2),inset 0 0 12px rgba(255,255,255,0.04);transition:box-shadow .3s ease,transform .3s ease}
    .cyber-card:hover{box-shadow:0 0 14px var(--cybg),0 0 28px var(--cybg2),inset 0 0 16px rgba(255,255,255,0.05)}
    .cyber-cyan{--cyb:rgba(0,190,205,0.32);--cybg:rgba(0,242,254,0.13);--cybg2:rgba(79,172,254,0.09);--cyico:#67e8f9}
    .cyber-green{--cyb:rgba(45,210,125,0.36);--cybg:rgba(52,255,150,0.16);--cybg2:rgba(52,255,150,0.1);--cyico:#6ee7b9}
    .cyber-gold{--cyb:rgba(215,160,45,0.4);--cybg:rgba(255,196,60,0.17);--cybg2:rgba(255,170,0,0.11);--cyico:#fcd34d}
    .cyber-pink{--cyb:rgba(215,0,140,0.36);--cybg:rgba(255,0,170,0.16);--cybg2:rgba(217,70,239,0.11);--cyico:#f9a8d4}
    .cyber-orange{--cyb:rgba(225,105,35,0.45);--cybg:rgba(255,120,40,0.19);--cybg2:rgba(249,115,22,0.12);--cybi:rgba(255,120,40,0.08);--cyico:#fdba74}
    .cyber-purple{--cyb:rgba(150,80,220,0.34);--cybg:rgba(168,85,247,0.14);--cybg2:rgba(139,92,246,0.09);--cyico:#d8b4fe}
    .cat-ico{color:var(--cyico,#67e8f9)}
    .cyber-card.cyber-night{box-shadow:0 0 16px var(--cybg),0 0 38px var(--cybg2),inset 0 0 12px rgba(255,255,255,0.06)}
    .cyber-card:active{transform:scale(0.97)}
    @keyframes cyshot{0%,100%{box-shadow:0 0 12px var(--cybg),0 0 26px var(--cybg2),inset 0 0 14px var(--cybi)}50%{box-shadow:0 0 22px var(--cybg),0 0 44px var(--cybg2),inset 0 0 20px var(--cybi)}}
    .cyber-pulse{animation:cyshot 1.6s ease-in-out infinite}
  `}</style>
  <button data-testid="strip-view-toggle" onClick={toggleView}
    className="absolute z-[1010] bottom-[calc(15rem+env(safe-area-inset-bottom))] left-3 rounded-full px-3 py-1.5 text-[11px] font-heading font-black text-white bg-gradient-to-r from-fuchsia-600 to-violet-600 shadow-lg active:scale-95 transition-transform">
    🎛️ LOGO VIEW
  </button>
  {/* 🔍 POG FINDER — type a name or category, the strip becomes your results */}
  <div className="absolute z-[1010] bottom-[calc(15rem+env(safe-area-inset-bottom))] right-3 flex items-center gap-1.5">
    {finderOpen && (
      <span className="flex items-center gap-1.5 rounded-full bg-slate-900/85 border border-cyan-400/50 backdrop-blur pl-3 pr-1.5 py-1 shadow-lg">
        <input autoFocus data-testid="pog-finder-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a merchant or category…"
          className="h-6 w-40 bg-transparent text-[11px] font-bold text-white placeholder:text-slate-500 focus:outline-none" />
        {ql && <span data-testid="pog-finder-count" className="text-[9px] font-heading font-black text-cyan-300 whitespace-nowrap">{finderBusinesses.filter((b) => !b.ad_image_url).length} found</span>}
      </span>
    )}
    <button data-testid="map-theme-btn" onClick={() => window.dispatchEvent(new CustomEvent("airpog:cycle-theme"))}
      className="w-8 h-8 rounded-full bg-slate-900/85 border border-violet-400/50 text-violet-300 text-[13px] backdrop-blur shadow-lg active:scale-95 transition-transform flex items-center justify-center" title="MAP THEME — day · twilight · satellite · NEON NIGHT">
      🌙
    </button>
    <button data-testid="pog-vision-btn" onClick={() => navigate("/vision")}
      className="relative w-8 h-8 rounded-full bg-slate-900/85 border border-amber-400/50 text-amber-300 text-[13px] backdrop-blur shadow-lg active:scale-95 transition-transform flex items-center justify-center" title="POG VISION — AR deals over the real street">
      👁️
      {dealsNearby > 0 && (
        <span data-testid="vision-deal-badge" className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-amber-400 text-slate-900 text-[8px] font-heading font-black flex items-center justify-center ring-2 ring-slate-900">
          {dealsNearby > 9 ? "9+" : dealsNearby}
        </span>
      )}
    </button>
    <button data-testid="pog-finder-toggle" onClick={() => { if (finderOpen) setQ(""); setFinderOpen(!finderOpen); }}
      className="w-8 h-8 rounded-full bg-slate-900/85 border border-cyan-400/50 text-cyan-300 text-[13px] backdrop-blur shadow-lg active:scale-95 transition-transform flex items-center justify-center">
      {finderOpen ? "✕" : "🔍"}
    </button>
  </div>
  {/* ⚡ POG PULSE — the zone's vital signs at a glance */}
  {(() => {
    const supers = businesses.filter((x) => x.super_pog).length;
    const rated = Object.values(starsMap).filter((s) => s.n > 0);
    const zoneAvg = rated.length ? (rated.reduce((a, s) => a + s.avg, 0) / rated.length).toFixed(1) : null;
    if (!supers && !zoneAvg) return null;
    return (
      <span data-testid="strip-pog-pulse" title="POG PULSE — this zone's vibe: avg POG'R rating + SUPER POG merchants nearby"
        className="absolute z-[1010] bottom-[calc(15rem+env(safe-area-inset-bottom))] left-[7.6rem] rounded-full px-2.5 py-1.5 text-[10px] font-heading font-black tracking-wide whitespace-nowrap bg-slate-900/80 text-fuchsia-200 backdrop-blur border border-fuchsia-400/40 shadow-lg">
        ⚡ PULSE{zoneAvg ? ` · ⭐ ${zoneAvg}` : ""}{supers ? ` · 🚀 ${supers} SUPER` : ""}
      </span>
    );
  })()}
  {/* 🔥 STRIP LIP — the live-deals counter rides the drawer's edge (Phase 1 remodel) */}
  {spotCount > 0 && (
    <div className={`absolute ${layout === "double" ? "bottom-[calc(26rem+env(safe-area-inset-bottom))]" : "bottom-[calc(17.5rem+env(safe-area-inset-bottom))]"} left-1/2 -translate-x-1/2 z-[1010] flex items-center gap-1.5 pointer-events-none`}>
      <button
        data-testid="strip-lip-counter"
        onClick={onToggleDeals}
        className={`pointer-events-auto flex items-center gap-1.5 rounded-full pl-3 pr-3 py-1.5 shadow-lg backdrop-blur font-heading font-bold text-xs whitespace-nowrap transition-colors ${dealsOnly ? "bg-amber-400 text-slate-900 ring-2 ring-amber-300" : "bg-slate-900/85 text-amber-300 border border-amber-400/40 hover:bg-slate-800/90"}`}
        title={dealsOnly ? "Showing deals only — tap for all spots" : "Tap to see only the live deals"}
      >
        <Flame className="w-3.5 h-3.5" />
        {dealCount} live {dealCount === 1 ? "deal" : "deals"}
        <span className={dealsOnly ? "text-slate-700" : "text-slate-500"}>· {spotCount} spots</span>
      </button>
      {zoneBadge && (
        <span data-testid="strip-lip-zone" className="pointer-events-auto rounded-full px-2.5 py-1 text-[10px] font-heading font-black tracking-wide whitespace-nowrap bg-emerald-400/25 text-emerald-200 backdrop-blur border border-emerald-400/30">
          {zoneBadge}
        </span>
      )}
    </div>
  )}
  <div
    ref={stripRef}
    data-testid={GPS.businessStrip}
    className={
      layout === "double"
        ? "absolute bottom-0 left-0 w-full z-[1000] overflow-x-auto no-scrollbar grid grid-rows-2 grid-flow-col auto-cols-max gap-3 items-end px-6 pt-10 pb-[calc(4.5rem+env(safe-area-inset-bottom))]"
        : layout === "horizontal"
        ? "absolute bottom-0 left-0 w-full z-[1000] overflow-x-auto no-scrollbar flex flex-row gap-4 items-end px-6 pt-10 pb-[calc(4.5rem+env(safe-area-inset-bottom))]"
        : "absolute right-0 top-0 h-full z-[1000] overflow-y-auto no-scrollbar flex flex-col gap-4 justify-start pt-24 pb-[calc(2rem+env(safe-area-inset-bottom))] pr-7 pl-10"
    }
  >
    {loading && businesses.length === 0 && (
      <div className="glass rounded-2xl px-5 py-4 flex items-center gap-3 shrink-0">
        {warming ? (
          <motion.img
            src={POGZILLA_BADGE}
            alt=""
            animate={{ y: [0, -6, 0], rotate: [0, -4, 0, 4, 0] }}
            transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut" }}
            className="w-8 h-8 object-contain shrink-0"
          />
        ) : (
          <Loader2 className="w-5 h-5 text-sky-400 animate-spin" />
        )}
        <span className="font-heading font-semibold text-sm">
          {warming ? "POGZILLA is waking the radar…" : `Scanning ${radius} mi around ${exploreActive ? exploreLabel : "you"}…`}
        </span>
      </div>
    )}
    {/* 📐 RENTED SPACES ride the strip too — garage sales, food trucks, mobile biz */}
    <RentedSlotCards />
    <NextEventCard />
    {shownBusinesses && finderBusinesses.map((b, i) => {
      const Ico = iconFor(b);
      const active = b.id === activeId;
      const adMode = !!b.ad_image_url;
      const spot = spotlightIds ? spotlightIds.get(b.id) : null; // 🎈 AUTO-FORGE spotlight on this merchant
      const spotted = !!spot;
      const spotTitle = spot?.offer?.title;
      const realDeal = spot?.offer?.kind === "public_promo"; // 🕵️ scouted/spotted REAL deal vs 🎈 check-in perk
      const logo = adMode || b.community ? null : logoFor(b, spot, BACKEND_URL); // 🎨 fair-use brand tile
      const webUrl = adMode || b.community ? null : webUrlFor(b, spot);
      const dealUrl = adMode || b.community ? null : dealUrlFor(b, spot); // 🎯 deep link to the brand's deals page
      const tapPing = (kind) => axios.post(`${BACKEND_URL}/api/tiles/${encodeURIComponent(b.id)}/tap?kind=${kind}`).catch(() => {});
      const slot = !adMode && !b.community && b.tile_slot?.type && b.tile_slot.type !== "auto" ? b.tile_slot : null; // 🎛️ Pro merchant slot
      const shot = shotMap[b.id]; // 🎯 POG SHOT — live flash blast from this merchant
      return (
        <Fragment key={b.id}>
        <button
          data-testid={GPS.businessTab}
          onClick={() => onTabClick(b)}
          style={{ animationDelay: `${Math.min(i * 30, 400)}ms` }}
          className={`fade-in shrink-0 w-[158px] md:w-[178px] cursor-pointer transition-all duration-300 hover:-translate-y-1.5 relative rounded-2xl overflow-hidden ${adMode ? "h-[150px] md:h-[168px] bg-slate-900" : `cyber-card ${nightGlow ? "cyber-night" : ""} ${b._motm ? "cyber-gold cyber-night" : shot ? "cyber-orange cyber-pulse" : b.featured || spotted ? "cyber-gold" : b.is_sale ? "cyber-pink" : b.has_deals ? "cyber-green" : poggedZone ? "cyber-cyan" : "cyber-purple"} flex flex-col text-left`} ${active ? "ring-2 ring-indigo-400" : ""}`}
        >
          {b._motm && !shot && (
            <span data-testid="strip-motm-badge" title="One rotating merchant takes the golden spotlight every minute!" className="absolute z-20 flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 text-[9px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2 whitespace-nowrap" style={{ top: 0 }}>
              ⭐ MERCHANT OF THE MINUTE
            </span>
          )}
          {shot ? (
            <span data-testid="strip-shot-badge" title={shot.message} className="absolute z-20 flex items-center gap-1 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2 animate-pulse" style={{ top: 0 }}>
              🎯 POG SHOT
            </span>
          ) : b.is_sale ? (
            <span className="absolute z-20 flex items-center gap-1 bg-fuchsia-500 text-white text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2" style={{ top: 0 }}>
              <PartyPopper className="w-3 h-3" /> {b.sale_type || "POG"}
            </span>
          ) : b.featured ? (
            <span className="absolute -top-0 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-amber-400 text-slate-900 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg" style={{ top: 0 }}>
              <Flame className="w-3 h-3" /> Featured
            </span>
          ) : spotted ? (
            realDeal ? (
              <span data-testid="strip-spotlight-badge" className="absolute z-20 flex items-center gap-1 bg-amber-400 text-slate-900 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2" style={{ top: 0 }}>
                🕵️ Spotted
              </span>
            ) : (
              <span data-testid="strip-perk-badge" className="absolute z-20 flex items-center gap-1 bg-sky-400 text-slate-900 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2" style={{ top: 0 }}>
                🎈 Perk
              </span>
            )
          ) : b.has_deals ? (
            <span className="absolute z-20 flex items-center gap-1 bg-emerald-500 text-slate-900 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2" style={{ top: 0 }}>
              <BadgePercent className="w-3 h-3" /> Deal
            </span>
          ) : b.community ? (
            <span className={`absolute z-20 flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-b-md shadow-lg left-1/2 -translate-x-1/2 ${b.verified ? "bg-emerald-500 text-white" : "bg-sky-500 text-white"}`} style={{ top: 0 }}>
              <Leaf className="w-3 h-3" /> {b.verified ? "Verified" : "New"}
            </span>
          ) : null}
          {!adMode && !b.community && !b.is_sale && (
            <span role="button" data-testid="instant-run-btn" title="INSTANT POG RUN — courier delivers this deal 🏃⚡"
              onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("airpog:instant-run", { detail: { b } })); }}
              className="absolute top-10 right-1.5 z-20 w-7 h-7 rounded-full flex items-center justify-center bg-emerald-400/20 hover:bg-emerald-400/35 transition-colors">
              <span className="text-[13px] leading-none">🏃</span>
            </span>
          )}
          <span
            role="button"
            data-testid="favorite-toggle"
            onClick={(e) => onFav(e, b)}
            className={`absolute top-1.5 right-1.5 z-20 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${adMode ? "bg-slate-900/50 hover:bg-slate-900/80" : "bg-white/10 hover:bg-white/20"}`}
            title={driver?.is_plus ? "Save favorite" : "Plus feature"}
          >
            <Heart className={`w-3.5 h-3.5 ${isFav(b.id) ? "fill-rose-500 text-rose-500" : "text-slate-300"} ${favBusy === b.id ? "animate-pulse" : ""}`} />
          </span>

          {adMode ? (
            b.ad_headline ? (
              <>
                {b.logo_url ? (
                  <img src={`${BACKEND_URL}${b.logo_url}`} alt={`AIR.POG map card: ${b.name}${b.ad_headline ? ` — ${b.ad_headline}` : ""}`} className="absolute inset-0 w-full h-full object-cover opacity-60" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <img src={`${BACKEND_URL}${b.ad_image_url}`} alt={`AIR.POG map card: ${b.name}${b.ad_headline ? ` — ${b.ad_headline}` : ""}`} className="absolute inset-0 w-full h-full object-cover opacity-70" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-950/85 via-slate-950/35 to-slate-950/90" />
                <span className="absolute top-1.5 left-2 z-10 text-[8px] font-extrabold uppercase tracking-[0.18em] text-white bg-fuchsia-500/80 rounded-full px-2 py-0.5">POG AD</span>
                <div className="absolute top-7 inset-x-0 z-10 px-2">
                  <p data-testid="pog-ad-headline" className="font-heading font-black text-[13px] leading-tight text-white text-center line-clamp-2" style={{ textShadow: "0 1px 5px rgba(0,0,0,0.9)" }}>{b.ad_headline}</p>
                </div>
                <div className="absolute bottom-0 inset-x-0 z-10 p-2.5 text-left">
                  <p className="font-heading font-bold text-[12px] leading-tight text-white line-clamp-1" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{b.name}{b.super_pog ? " 🚀" : ""}</p>
                  <p className="text-sky-300 text-[11px] font-bold" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{b.distance_miles} mi · tap for more 🎯</p>
                </div>
              </>
            ) : (
              <>
                <img src={`${BACKEND_URL}${b.ad_image_url}`} alt={`AIR.POG map card: ${b.name}${b.distance_miles != null ? ` ${b.distance_miles} mi` : ""}`} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-slate-950/10" />
                <span className="absolute top-1.5 left-2 z-10 text-[9px] font-extrabold uppercase tracking-widest text-white/90 bg-white/10 backdrop-blur-sm rounded px-1.5 py-0.5">Ad</span>
                <div className="absolute bottom-0 inset-x-0 z-10 p-3 text-left">
                  <p className="font-heading font-bold text-sm leading-tight text-white line-clamp-2" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{b.name}{b.super_pog ? " 🚀" : ""}</p>
                  <p className="text-sky-300 text-xs font-bold mt-0.5" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{b.distance_miles} mi</p>
                </div>
              </>
            )
          ) : (
            <>
              <div className="p-3.5 pb-2 flex-1 w-full">
                <div className="flex items-start gap-2">
                  <div className={`shrink-0 rounded-lg p-1.5 border ${active ? "bg-white/15 border-white/25" : "bg-white/10 border-white/15"}`}>
                    {b.logo_url ? (
                      <img src={`${BACKEND_URL}${b.logo_url}`} alt={`${b.name} logo`} className="w-5 h-5 object-contain rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <Ico className="w-5 h-5 cat-ico" />
                    )}
                  </div>
                  <p className="font-heading font-extrabold text-[15px] text-white leading-tight line-clamp-2 min-w-0">{b.name}</p>
                  {b.super_pog && <span data-testid="strip-super-badge" title="SUPER POG MERCHANT — annual pro" className="shrink-0 text-[13px] leading-none mt-0.5">🚀</span>}
                </div>
                <p className="text-[10px] text-white/90 font-bold uppercase tracking-wider mt-2 truncate">{t(`categories.${b.category_id}`, { defaultValue: b.category_label })}</p>
                <p className="text-white text-sm font-bold mt-1 flex items-center gap-1.5">{b.distance_miles} mi
                  {starsMap[b.id]?.n > 0 && (
                    <span data-testid="strip-stars-tag" title={`${starsMap[b.id].n} POG'R rating${starsMap[b.id].n === 1 ? "" : "s"}`}
                      className="inline-flex items-center gap-0.5 rounded-full bg-yellow-400/15 border border-yellow-400/40 text-yellow-300 text-[9px] font-heading font-black px-1.5 py-0.5 leading-none">
                      ⭐ {starsMap[b.id].avg}
                    </span>
                  )}
                  {marketByBiz.has(b.id) && (
                    <span data-testid="strip-market-tag" title="This merchant sells POG MARKET rewards — spend your BUCKS here!"
                      className="inline-flex items-center gap-0.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[9px] font-heading font-black px-1.5 py-0.5 leading-none">
                      🛒 REWARDS
                    </span>
                  )}
                </p>
              </div>
              <div className="border-t border-white/10 w-full">
                {b.community ? (
                  b.verified ? (
                    <div className="px-3.5 py-2 flex items-center gap-1.5">
                      <Leaf className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-[11px] font-heading font-extrabold text-emerald-400">Community verified</span>
                    </div>
                  ) : hasVoted(b.id) ? (
                    <div className="px-3.5 py-2">
                      <span className="text-[11px] font-heading font-bold text-white/80">Thanks for confirming! 🌱</span>
                    </div>
                  ) : (
                    <div className="px-2.5 py-2 flex items-center justify-between gap-1">
                      <span className="text-[10px] font-heading font-bold text-white/80 leading-tight">Added by a POG'R — correct?</span>
                      <span className="flex gap-1 shrink-0">
                        <span role="button" data-testid="biz-vote-up" onClick={(e) => voteBiz(e, b, "up")} title="Yes, it's here" className="w-7 h-7 rounded-full bg-emerald-400/15 hover:bg-emerald-400/30 flex items-center justify-center">
                          <ThumbsUp className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                        <span role="button" data-testid="biz-vote-down" onClick={(e) => voteBiz(e, b, "down")} title="Not there" className="w-7 h-7 rounded-full bg-rose-400/15 hover:bg-rose-400/30 flex items-center justify-center">
                          <ThumbsDown className="w-3.5 h-3.5 text-rose-400" />
                        </span>
                      </span>
                    </div>
                  )
                ) : logo || spotted || slot ? (
                  /* 🎛️ TWIN TILE — Sir Eric's decree: logo · deals-inside ONLY (center tile removed) */
                  <div className="px-1.5 py-1.5 flex items-stretch gap-1">
                    <span role="button" data-testid="strip-logo-tile" title="See this merchant's SALES & OFFERS 🎯"
                      onClick={(e) => { e.stopPropagation(); tapPing("logo"); onTabClick(b); }}
                      className="relative w-[4.25rem] shrink-0 rounded-lg border border-cyan-400/30 bg-slate-800/70 flex items-center justify-center overflow-hidden min-h-[4rem] cursor-pointer hover:ring-2 hover:ring-cyan-400/60">
                      <span className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
                        <span className="font-heading font-black text-lg cat-ico leading-none">{(b.name || "?").charAt(0).toUpperCase()}</span>
                        <Ico className="w-3.5 h-3.5 cat-ico opacity-70" />
                      </span>
                      {logo && (
                        <img src={logo} alt={`${b.name} logo`} loading="lazy" className="absolute inset-0 w-full h-full object-contain bg-white scale-[1.18]" onError={(e) => { e.currentTarget.style.display = "none"; }}
                          onLoad={(e) => { if (e.currentTarget.naturalWidth > 0 && e.currentTarget.naturalWidth < 40) e.currentTarget.className = "absolute inset-0 m-auto w-12 h-12 object-contain bg-white rounded"; }} />
                      )}
                    </span>
                    {slot ? (
                      <span role="button" data-testid="strip-action-tile"
                        onClick={(e) => {
                          e.stopPropagation();
                          axios.post(`${BACKEND_URL}/api/tiles/${encodeURIComponent(b.id)}/tap`).catch(() => {});
                          if (slot.url) window.open(slot.url, "_blank", "noopener");
                          else if (slot.type === "flics" || slot.type === "live") navigate("/flics");
                          else if (webUrl) window.open(webUrl, "_blank", "noopener");
                        }}
                        className="flex-1 min-w-0 rounded-lg bg-gradient-to-b from-violet-600/20 to-fuchsia-500/20 border border-violet-400/50 hover:from-violet-600/35 hover:to-fuchsia-500/35 px-1 flex flex-col items-center justify-center text-center">
                        <span className="text-[13px] leading-none">{{ web: "🌐", app: "📱", order: "🛒", flics: "🔥", live: "📡", link: "🔗" }[slot.type] || "✨"}</span>
                        <span className="w-full text-[8px] font-heading font-extrabold text-white leading-tight truncate mt-0.5">
                          {slot.label || { web: "Our site", app: "Get the app", order: "Order now", flics: "Pics & Flics", live: "Watch live", link: "Tap in" }[slot.type] || "Tap in"}
                        </span>
                      </span>
                    ) : webUrl ? (
                      /* 🔥 The money move — straight to the merchant's deals page where they ~100% live */
                      brandDomain(b.name) ? (
                        <span role="button" data-testid="strip-action-tile" onClick={(e) => { e.stopPropagation(); tapPing("website"); window.open(dealUrl || webUrl, "_blank", "noopener"); }} className="flex-1 min-w-0 rounded-lg bg-orange-400/10 border border-orange-400/40 hover:bg-orange-400/25 px-1 flex flex-col items-center justify-center text-center">
                          <span className="text-[13px] leading-none">🔥</span>
                          <span className="w-full text-[8px] font-heading font-extrabold text-white leading-tight truncate mt-0.5">Deals inside ↗</span>
                        </span>
                      ) : (
                        <span role="button" data-testid="strip-action-tile" onClick={(e) => { e.stopPropagation(); tapPing("website"); window.open(webUrl, "_blank", "noopener"); }} className="flex-1 min-w-0 rounded-lg bg-slate-700/50 border border-slate-500/40 hover:bg-slate-600/60 px-1 flex flex-col items-center justify-center text-center">
                          <span className="text-[13px] leading-none">🌐</span>
                          <span className="w-full text-[8px] font-heading font-extrabold text-white leading-tight truncate mt-0.5">Website</span>
                        </span>
                      )
                    ) : spotted ? (
                      <span role="button" data-testid="strip-flics-tile" title="This shop's Pics & Flics 🔥"
                        onClick={(e) => { e.stopPropagation(); window.location.assign(`/flics?biz=${encodeURIComponent(b.id)}&bizname=${encodeURIComponent(b.name || "")}&blat=${b.lat ?? ""}&blng=${b.lng ?? ""}`); }}
                        className="flex-1 min-w-0 rounded-lg bg-orange-400/10 border border-orange-400/40 hover:bg-orange-400/25 flex items-center justify-center">
                        <span className="text-lg leading-none">🔥</span>
                      </span>
                    ) : (
                      <span role="button" data-testid="strip-action-tile" onClick={(e) => { e.stopPropagation(); navigate("/business"); }} className="flex-1 min-w-0 rounded-lg border border-dashed border-fuchsia-400/50 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 px-1 flex flex-col items-center justify-center text-center">
                        <span className="text-[13px] leading-none">＋</span>
                        <span className="w-full text-[8px] font-heading font-extrabold text-fuchsia-300 leading-tight truncate mt-0.5">Own this — free</span>
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="px-3.5 py-2">
                    {spotted ? (
                      <span data-testid="strip-deal-preview" className="text-[12px] font-heading font-extrabold block truncate text-white">
                        {realDeal ? "🕵️" : "🎈"} {spotTitle || "Deal spotlight — tap!"}
                      </span>
                    ) : (
                      <span className="text-[12px] font-heading font-extrabold text-white leading-tight block">Earn/Redeem<br />POG BUCKS</span>
                    )}
                  </div>
                )}
              </div>
              {/* 💥 POG'NIHLATION CHIP — Google-style contextual action tag, 5-for-5 harvested */}
              {!b.community && (
                <span role="button" data-testid="strip-cta-chip"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (dealUrl || (b.has_deals && webUrl)) { tapPing("cta"); window.open(dealUrl || webUrl, "_blank", "noopener"); }
                    else window.dispatchEvent(new CustomEvent("airpog:instant-run", { detail: { b } }));
                  }}
                  className={`mx-1.5 mb-1.5 h-[22px] shrink-0 rounded-md flex items-center justify-center gap-1 text-[9px] font-heading font-black tracking-wide cursor-pointer transition-colors ${dealUrl || (b.has_deals && webUrl) ? "bg-orange-400/20 border border-orange-400/50 text-orange-200 hover:bg-orange-400/35" : "bg-emerald-400/15 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/30"}`}>
                  {dealUrl || (b.has_deals && webUrl) ? "🔥 GRAB THE DEAL ↗" : "⚡ POG RUN IT — courier it"}
                </span>
              )}
            </>
          )}
        </button>
        {(i + 1) % 8 === 0 && i + 1 < shownBusinesses.length && <AdSpotCard navigate={navigate} />}
        </Fragment>
      );
    })}
    {!loading && businesses.length > 0 && <AdSpotCard navigate={navigate} />}
    {!loading && businesses.length > 0 && (
      <div className="shrink-0 self-center text-[11px] text-slate-500 px-2 max-w-[120px] leading-snug">
        Tap to route · Double-tap for deals · Tap the map to explore there
      </div>
    )}
  </div>
  </>
  );
};
